// Header — the shared wall header shell (lifted from Toile's own `components/Header`, plan
// `2026_09_17_ovid_one_wall.md`). `.brand`/`.nav`/`.right` are the slots a house's own composition
// fills; the chrome around them (sticky, reveal, spacing) is this file's only concern.

// INCIDENT (plan 2026_09_04_wall_header_reveal.md § Step 0, full derivation in
// products/toile/styles/index.scss's own comment on the fix): `position: sticky; top: 0` here
// declared sticky and did not stick, on toile dev AND on narcis.ai (measured 2026-09-04 — the
// header's own `getBoundingClientRect().top` tracked `-window.scrollY` 1:1 at any depth, i.e. it
// behaved exactly like `position: relative`). Cause: quies's `products/quies/styles/index.scss`
// sets `body{overflow-x:hidden}`/`html{overflow-x:hidden}` UNSCOPED, and every product's index.scss
// ships in the same bundle regardless of `PRODUCT_NAME` (`app/root.tsx`) — the non-visible overflow
// makes `<body>` a scroll container (CSS Overflow §3.3: either axis non-visible computes the other
// to `auto`), and `<body>`'s own box always sizes exactly to its content, so it never itself has
// anything to scroll — but merely being flagged that way makes it THIS header's nearest scrolling
// ancestor for `position: sticky`'s reference-frame lookup, ahead of `<html>` (the real,
// viewport-sized scrollport one level further out). Fixed at that layer, not here.
.header {
    position: sticky;
    top: 0;
    // z-index: `--tl-z-header` (a house's own stylesheet — the wall's stacking order, one source).
    z-index: var(--tl-z-header);
    display: flex;
    align-items: center;
    gap: 22px;
    padding: 13px 26px;
    border-bottom: 1px solid var(--tl-border);
    // `--tl-stage-veil` — a house's own token (`--tl-stage`'s colour at this alpha) so this rule
    // stays colour-neutral.
    background: var(--tl-stage-veil);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    transition: transform 180ms ease;

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
}

// The reveal (plan § Step 1) — the header never leaves the flow (`position: sticky` stays), so
// `Wall.tsx`'s windowing (which reads `getBoundingClientRect`) is untouched by hiding it this way.
.headerHidden {
    transform: translateY(-100%);
}

// While the search panel stands as a sheet over the screen: a blurred or transformed header would become the sheet's
// containing block, and the sheet is placed against the screen.
.headerSheet {
    transform: none;
    transition: none;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
}

// Present only once floating over content rather than sitting above it (`atTop` false).
.headerFloating {
    box-shadow: 0 8px 24px color-mix(in srgb, var(--tl-shadow-ink) 35%, transparent);
}

.brand {
    display: flex;
    align-items: center;
    gap: 9px;
}

.nav {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 22px;
}

// Right rail — sits after the nav, which keeps its own margin-left: auto so the pair is pushed to
// the far edge together (Chrome.dc.html's `.hd .right`). Carries its OWN margin-left: auto too, so
// a house with no `nav` slot (Ovid) still gets the rail pushed to the far edge on its own; cancelled
// back to 0 below (desktop only) once a nav sibling exists, so the two don't open a gap between them
// that the design never had.
.right {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-left: auto;
}

@media (min-width: 641px) {
    .nav + .right {
        margin-left: 0;
    }
}

@media (max-width: 640px) {
    .header {
        padding: 11px 16px;
        gap: 14px;
        // The lean stands on a second line of the header (`HeaderSearch`'s `.leanLine`).
        flex-wrap: wrap;
        row-gap: 0;
    }

    // A house's own nav door moves into its account menu at this width (Toile: `UserBlock`'s Packs
    // item) — stays IN THE DOM (`display: none`, not unmounted) so the endpaper's structure and a
    // crawler both still see it at every width. `.right` keeps the base rule's own `margin-left:
    // auto` here (the `.nav + .right` reset above is desktop-only), exactly reproducing the
    // pre-move behaviour where a hidden-but-present `.nav` no longer carries the push.
    .nav {
        display: none;
    }

    // While the field has focus the field is the row (designer proposal "Pills in the bar": pills and
    // words need the width): the wordmark and the rail step aside, as the folded field already makes
    // them. The `:has` rule is a progressive enhancement; without it the row simply stays shared. A field with a
    // panel open is a sheet over the screen instead and leaves the header as it is.
    .header:has(input:focus):not(:has([data-open])) .brand,
    .header:has(input:focus):not(:has([data-open])) .right {
        display: none;
    }

    // Legacy `'fold'` support (`HeaderSearch`'s own `phone` prop default, edge case 24) — no caller
    // triggers `.headerSearching` today (both houses pass `phone="open"`), but a future one that
    // keeps the fold still gets the brand/nav/rail standing down while its glyph is unfolded.
    .headerSearching .brand,
    .headerSearching .nav,
    .headerSearching .right {
        display: none;
    }
}
